
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Bell, BellOff, CheckCheck } from "lucide-react";
import type { Notification } from "@/types/notification";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";

export default function Notifications() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isMuted, setIsMuted] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Fetch initial mute status
    const fetchMuteStatus = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('notifications_muted')
        .eq('id', user.id)
        .single();
      
      if (profile) {
        setIsMuted(profile.notifications_muted);
      }
    };
    
    fetchMuteStatus();
  }, []);

  const { data: notifications, isLoading, refetch } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Notification[];
    }
  });

  const handleMarkAsRead = async () => {
    if (!selectedIds.length) return;

    try {
      const { error } = await supabase.rpc('mark_notifications_as_read', {
        notification_ids: selectedIds
      });

      if (error) throw error;
      
      toast.success("Notifications marked as read");
      setSelectedIds([]);
      await Promise.all([
        refetch(),
        queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] })
      ]);
    } catch (error: any) {
      toast.error("Failed to mark notifications as read");
      console.error('Error:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const { error } = await supabase.rpc('mark_all_notifications_as_read', {
        user_id_input: (await supabase.auth.getUser()).data.user?.id
      });

      if (error) throw error;
      
      toast.success("All notifications marked as read");
      setSelectedIds([]);
      await Promise.all([
        refetch(),
        queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] })
      ]);
    } catch (error: any) {
      toast.error("Failed to mark all notifications as read");
      console.error('Error:', error);
    }
  };

  const toggleMuteNotifications = async () => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ notifications_muted: !isMuted })
        .eq('id', (await supabase.auth.getUser()).data.user?.id);

      if (error) throw error;
      
      setIsMuted(!isMuted);
      toast.success(isMuted ? "Notifications unmuted" : "Notifications muted");
    } catch (error: any) {
      toast.error("Failed to update notification settings");
      console.error('Error:', error);
    }
  };

  const handleNavigate = (notification: Notification) => {
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const NotificationCard = ({ notification }: { notification: Notification }) => (
    <Card 
      key={notification.id}
      className={`cursor-pointer transition-colors hover:bg-muted/50 ${
        !notification.read ? 'border-primary' : ''
      }`}
      onClick={() => handleNavigate(notification)}
    >
      <CardContent className={`${isMobile ? 'p-4' : 'p-6'} flex items-start gap-4`}>
        <input
          type="checkbox"
          checked={selectedIds.includes(notification.id)}
          onChange={(e) => {
            e.stopPropagation();
            setSelectedIds(prev => 
              e.target.checked 
                ? [...prev, notification.id]
                : prev.filter(id => id !== notification.id)
            );
          }}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="font-medium">{notification.title}</div>
          <p className={`${isMobile ? 'text-sm' : ''} text-muted-foreground mt-1`}>
            {notification.message}
          </p>
          <div className="text-xs text-muted-foreground mt-2">
            {new Date(notification.created_at).toLocaleDateString()} at{" "}
            {new Date(notification.created_at).toLocaleTimeString()}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="container py-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const unreadNotifications = notifications?.filter(n => !n.read) || [];
  const readNotifications = notifications?.filter(n => n.read) || [];

  return (
    <div className={`container py-8 ${isMobile ? 'px-2 max-w-full' : 'max-w-3xl'}`}>
      <div className={`flex items-center justify-between mb-6 ${isMobile ? 'flex-col gap-4' : ''}`}>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="h-6 w-6" />
          Notifications
        </h1>
        <div className={`flex gap-2 ${isMobile ? 'w-full flex-col' : ''}`}>
          <Button
            variant="outline"
            onClick={toggleMuteNotifications}
            className="gap-2"
            size={isMobile ? "sm" : "default"}
          >
            {isMuted ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
            {isMuted ? 'Unmute' : 'Mute'} Notifications
          </Button>
          <Button
            variant="outline"
            onClick={handleMarkAllAsRead}
            className="gap-2"
            size={isMobile ? "sm" : "default"}
          >
            <CheckCheck className="h-4 w-4" />
            Mark All as Read
          </Button>
          {selectedIds.length > 0 && (
            <Button 
              onClick={handleMarkAsRead}
              size={isMobile ? "sm" : "default"}
              className="w-full"
            >
              Mark {selectedIds.length} as Read
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="unread" className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="unread" className="flex-1">
            Unread ({unreadNotifications.length})
          </TabsTrigger>
          <TabsTrigger value="read" className="flex-1">
            Read ({readNotifications.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="unread">
          <div className="space-y-3">
            {unreadNotifications.length === 0 ? (
              <Card>
                <CardContent className={`${isMobile ? 'p-4' : 'p-6'} text-center text-muted-foreground`}>
                  No unread notifications
                </CardContent>
              </Card>
            ) : (
              unreadNotifications.map((notification) => (
                <NotificationCard key={notification.id} notification={notification} />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="read">
          <div className="space-y-3">
            {readNotifications.length === 0 ? (
              <Card>
                <CardContent className={`${isMobile ? 'p-4' : 'p-6'} text-center text-muted-foreground`}>
                  No read notifications
                </CardContent>
              </Card>
            ) : (
              readNotifications.map((notification) => (
                <NotificationCard key={notification.id} notification={notification} />
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
